import { ArrowLeftIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useParams } from 'react-router-dom'

import { ConnectorDetailSkeleton } from '@/components/connectors/connector-detail-skeleton.atom'
import { ConnectorProviderCardContent } from '@/components/connectors/connector-provider-card-content.atom'
import { ConnectorProviderHeader } from '@/components/connectors/connector-provider-header.molecule'
import { DisconnectConnectorDialog } from '@/components/connectors/disconnect-connector-dialog.molecule'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'

import { ConnectorDetailProjects } from './connector-detail-projects.view'
import { useConnectorDetail } from './use-connector-detail.hook'
import { useConnectorDetailNavigation } from './use-connector-detail-navigation.hook'

export const ConnectorDetailView: React.FC = (): ReactNode => {
  const { providerSlug } = useParams<{ providerSlug: string }>()
  const detail = useConnectorDetail(providerSlug ?? '')
  const { navigateBack } = useConnectorDetailNavigation(detail)

  if (detail.isLoading) return <ConnectorDetailSkeleton />
  if (!detail.provider) return null

  return (
    <div className="connector-detail-view space-y-6">
      <Button
        variant="ghost"
        size="sm"
        className="connector-back-button -ml-2 gap-1"
        onClick={navigateBack}
      >
        <ArrowLeftIcon className="size-4" />
        Back to Connectors
      </Button>
      <ConnectorProviderHeader
        providerSlug={detail.provider.slug}
        providerDisplayName={detail.provider.displayName}
        serviceTypeList={detail.provider.serviceTypeList}
        isConnected={detail.provider.isConnected}
        connectedAccountEmail={detail.connection?.providerAccountId ?? null}
      />
      <Separator />
      <ConnectorProviderCardContent
        providerSlug={detail.provider.slug}
        providerDisplayName={detail.provider.displayName}
        isConnected={detail.provider.isConnected}
        connectedAccountEmail={detail.connection?.providerAccountId ?? null}
        isConnecting={detail.connectingProviderSlug === detail.provider.slug}
        onConnect={detail.handleConnect}
        onDisconnect={detail.handleDisconnect}
      />
      <ConnectorDetailProjects
        connection={detail.connection}
        projectList={detail.projectList}
        onToggleActive={detail.handleToggleProjectActive}
      />
      <DisconnectConnectorDialog
        connection={detail.connectionToDisconnect}
        onClose={detail.handleDisconnectCancel}
        onConfirm={detail.handleDisconnectConfirm}
      />
    </div>
  )
}

ConnectorDetailView.displayName = 'ConnectorDetailView'
